import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyCompanyStats } from '../hooks/useCompany';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PlanUsageWidget from '../components/company/PlanUsageWidget';
import { formatLimit, isUnlimited } from '../utils/planLimits';

const MyCompany: React.FC = () => {
  const { data: statsData, isLoading, error } = useMyCompanyStats();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !statsData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
        <p className="text-red-700">No se pudo cargar la información de la empresa.</p>
      </div>
    );
  }

  const { company, stats, alerts } = statsData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {company.tradeName || company.rut}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            company.settings.subscriptionStatus === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {company.settings.subscriptionStatus === 'active' ? 'Activa' : 'Suspendida'}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            Plan {company.settings.subscriptionPlan}
          </span>
        </div>
      </div>

      {/* Plan Usage Widget */}
      <PlanUsageWidget showPrice={true} />
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Users Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Usuarios</h3>
            <span className="text-2xl">👥</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Activos:</span>
              <span className="font-semibold text-green-600">{stats.users.active}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Inactivos:</span>
              <span className="font-semibold text-gray-600">{stats.users.inactive}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Límite:</span>
              <span className="font-semibold text-blue-600">
                {formatLimit(stats.users.limit)}
              </span>
            </div>
            {!isUnlimited(stats.users.limit) && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Uso</span>
                <span>{stats.users.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    stats.users.percentage >= 90 ? 'bg-red-500' :
                    stats.users.percentage >= 70 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${stats.users.percentage}%` }}
                />
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Projects Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Proyectos</h3>
            <span className="text-2xl">📁</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Activos:</span>
              <span className="font-semibold text-green-600">{stats.projects.active}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Completados:</span>
              <span className="font-semibold text-blue-600">{stats.projects.completed}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Suspendidos:</span>
              <span className="font-semibold text-yellow-600">{stats.projects.suspended}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold pt-2 border-t">
              <span className="text-gray-900">Total:</span>
              <span className="text-gray-900">{stats.projects.total}</span>
            </div>
          </div>
        </div>

        {/* Expenses Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Gastos</h3>
            <span className="text-2xl">💰</span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Mes actual:</span>
              <span className="font-semibold text-gray-900">
                ${stats.expenses.currentMonth.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Mes anterior:</span>
              <span className="font-semibold text-gray-600">
                ${stats.expenses.lastMonth.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total registros:</span>
              <span className="font-semibold text-blue-600">{stats.expenses.count}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold pt-2 border-t">
              <span className="text-gray-900">Total histórico:</span>
              <span className="text-gray-900">
                ${stats.expenses.total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Over Budget Projects */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Proyectos sobre presupuesto</h3>
            <span className="text-2xl">🔴</span>
          </div>
          {alerts?.overBudgetProjects?.length > 0 ? (
            <div className="space-y-3">
              {alerts.overBudgetProjects.map((p: any) => (
                <div
                  key={p.projectId}
                  onClick={() => navigate(`/projects/${p.projectId}`)}
                  className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500">
                      Presupuesto: ${p.budget.toLocaleString()} · Gastado: ${p.spent.toLocaleString()}
                    </p>
                  </div>
                  <span className="ml-3 text-sm font-semibold text-red-600 whitespace-nowrap">
                    +${p.overBy.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <span className="text-3xl">✅</span>
              <p className="mt-2 text-sm text-gray-500">Todos los proyectos dentro del presupuesto</p>
            </div>
          )}
        </div>

        {/* Due Soon Projects */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Proyectos por vencer</h3>
            <span className="text-2xl">⏰</span>
          </div>
          {alerts?.dueSoonProjects?.length > 0 ? (
            <div className="space-y-3">
              {alerts.dueSoonProjects.map((p: any) => {
                const isOverdue = p.daysLeft < 0;
                const isUrgent = p.daysLeft >= 0 && p.daysLeft <= 7;
                const bgColor = isOverdue ? 'bg-red-50 border-red-100' : isUrgent ? 'bg-yellow-50 border-yellow-100' : 'bg-orange-50 border-orange-100';
                const textColor = isOverdue ? 'text-red-600' : isUrgent ? 'text-yellow-600' : 'text-orange-600';
                const label = isOverdue
                  ? `Vencido hace ${Math.abs(p.daysLeft)} día${Math.abs(p.daysLeft) !== 1 ? 's' : ''}`
                  : p.daysLeft === 0
                    ? 'Vence hoy'
                    : `${p.daysLeft} día${p.daysLeft !== 1 ? 's' : ''} restante${p.daysLeft !== 1 ? 's' : ''}`;

                return (
                  <div
                    key={p.projectId}
                    onClick={() => navigate(`/projects/${p.projectId}`)}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:opacity-80 transition-opacity ${bgColor}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500">
                        Vencimiento: {new Date(p.dueDate + 'T00:00:00').toLocaleDateString('es-CL')}
                      </p>
                    </div>
                    <span className={`ml-3 text-sm font-semibold whitespace-nowrap ${textColor}`}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <span className="text-3xl">✅</span>
              <p className="mt-2 text-sm text-gray-500">No hay proyectos próximos a vencer</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCompany;
